import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { studentAuth } from '../api/client';

export default function Welcome() {
  const navigate = useNavigate();
  // mode: null=还没选身份, 'official'=项目学生, (访客直接跳转,不停留)
  const [mode, setMode] = useState<'official' | null>(null);
  const [form, setForm] = useState({ last_name: '', first_name: '', student_id: '', gender: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!form.last_name || !form.first_name || !form.student_id || !form.gender) {
      setError('请填写所有信息');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/session/start', form);
      sessionStorage.setItem('user_id', res.data.id);
      sessionStorage.setItem('user_name', res.data.name);
      // 存学生身份 token(后续 results/roommate 接口鉴权用)
      if (res.data.token) {
        studentAuth.set(res.data.token);
      }

      // 智能跳转:已答完 → 直接看结果(也是配对入口),未答完 → 去答题
      if (res.data.has_completed) {
        navigate(`/result/${res.data.id}`);
      } else {
        navigate('/quiz');
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || '提交失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // ============ 身份选择页 ============
  if (mode === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
          <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">书院分配心测</h1>
          <p className="text-gray-500 text-center mb-8">请选择进入方式</p>

          <div className="space-y-4">
            <button
              onClick={() => setMode('official')}
              className="w-full bg-indigo-600 text-white py-4 rounded-xl font-medium hover:bg-indigo-700 transition-colors text-left px-5"
            >
              <div className="text-lg">暑期学院项目学生</div>
              <div className="text-xs text-indigo-100 mt-1">用报名名单的姓名和学号登录,参与真实书院分配与室友配对</div>
            </button>

            <button
              onClick={() => navigate('/guest-quiz')}
              className="w-full bg-white border-2 border-indigo-200 text-indigo-700 py-4 rounded-xl font-medium hover:border-indigo-400 transition-colors text-left px-5"
            >
              <div className="text-lg">自由体验</div>
              <div className="text-xs text-gray-500 mt-1">无需登录,做完测试即可查看你与九个书院的性格匹配度(不参与分配)</div>
            </button>
          </div>

          <p className="text-xs text-gray-400 text-center mt-6">
            💡 暑期学院项目学生请选择第一项,以确保结果计入正式分配
          </p>
        </div>
      </div>
    );
  }

  // ============ 项目学生登录表单 ============
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
        <button
          onClick={() => { setMode(null); setError(''); }}
          className="text-sm text-gray-400 hover:text-gray-600 mb-4"
        >
          ← 返回
        </button>
        <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">暑期学院项目学生登录</h1>
        <p className="text-gray-500 text-center mb-8">请填写以下信息开始测试</p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">姓 Last Name</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="例如：CHEN"
              value={form.last_name}
              onChange={e => setForm({ ...form, last_name: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">名 First Name</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="例如：ZENING"
              value={form.first_name}
              onChange={e => setForm({ ...form, first_name: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">学号</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="例如：Z123456"
              value={form.student_id}
              onChange={e => setForm({ ...form, student_id: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">性别</label>
            <div className="flex gap-4">
              {[{ label: '男', value: 'male' }, { label: '女', value: 'female' }].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setForm({ ...form, gender: opt.value })}
                  className={`flex-1 py-2 rounded-lg border-2 font-medium transition-all ${
                    form.gender === opt.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:border-indigo-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 mt-4"
          >
            {loading ? '提交中...' : '开始测试'}
          </button>

          <p className="text-xs text-gray-400 text-center mt-2">
            💡 请填写与报名名单一致的姓名拼音和学号
          </p>
        </div>
      </div>
    </div>
  );
}
